import React, { useMemo } from 'react';
import { filter, isEmpty } from 'lodash';
import { Button, Spinner } from 'tuui';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { CONVERSION_STATUS } from 'modules/tracking/config/constants';
import { STEP_IDS } from '../../config/stepIds';
import { useConversions } from '../../hooks/useConversions';
import { useSaveAndClose, ERROR_TYPES } from '../../hooks/useSaveAndClose';
import { DuplicateNameErrorModal } from './ErrorModals/DuplicateNameErrorModal';
import { GeneralErrorModal } from './ErrorModals/GeneralErrorModal';
import { SessionTokenExpiredModal } from './ErrorModals/SessionTokenExpiredModal';
import { EventReviewSection } from './EventReviewSection';
import styles from './reviewEvents.module.scss';

export const ReviewEvents = ({
    navigateToStepId,
    handleEditEvent,
    setFocusedConversionId,
    unfilteredFetchedConversions,
}) => {
    const { conversionsToSave } = useConversions();

    const { isLoading, showErrorModal, errorType, duplicateConversionName, handleSaveAndClose, hideErrorModal } =
        useSaveAndClose({
            conversionsToSave,
            navigateToStepId,
            unfilteredFetchedConversions,
        });

    const handleGoBack = () => {
        navigateToStepId(STEP_IDS.VIEW_EVENTS_LIST);
    };

    const hasChangesToSave = !isEmpty(conversionsToSave);

    const createdAndEditedEvents = useMemo(() => {
        return filter(conversionsToSave, event => event.status !== CONVERSION_STATUS.ARCHIVED);
    }, [conversionsToSave]);

    const deletedEvents = useMemo(() => {
        return filter(conversionsToSave, event => event.status === CONVERSION_STATUS.ARCHIVED);
    }, [conversionsToSave]);

    const handleRetry = () => {
        hideErrorModal();
        handleSaveAndClose();
    };

    const handleBackFromError = () => {
        hideErrorModal();
        handleGoBack();
    };

    if (showErrorModal) {
        switch (errorType) {
            case ERROR_TYPES.DUPLICATE_NAME:
                return (
                    <DuplicateNameErrorModal
                        duplicateConversionName={duplicateConversionName}
                        onBack={handleBackFromError}
                    />
                );
            case ERROR_TYPES.SESSION_TOKEN_EXPIRED:
                return <SessionTokenExpiredModal />;
            default:
                return <GeneralErrorModal onRetry={handleRetry} />;
        }
    }

    return (
        <div>
            <div className={styles['events-wrapper']}>
                <div className={styles['container']}>
                    <div className={styles['title']}>
                        <FormattedMessage id="codeless.review.event.header" defaultMessage="Review Changes" />
                    </div>
                </div>
                <div className={styles['review-sections-container']}>
                    <EventReviewSection
                        titleId="codeless.review.event.subtitle.header"
                        titleDefaultMessage="Events created and edited"
                        events={createdAndEditedEvents}
                        onEditEvent={handleEditEvent}
                        onSetFocusedConversionId={setFocusedConversionId}
                    />

                    <EventReviewSection
                        titleId="codeless.review.event.deleted.header"
                        titleDefaultMessage="Events deleted"
                        events={deletedEvents}
                        onEditEvent={handleEditEvent}
                        onSetFocusedConversionId={setFocusedConversionId}
                    />

                    {createdAndEditedEvents.length === 0 && deletedEvents.length === 0 && (
                        <div className={styles['no-changes']}>
                            <FormattedMessage id="codeless.review.no.changes" defaultMessage="No changes to review." />
                        </div>
                    )}
                </div>
            </div>

            <footer className={styles['footer']}>
                <Button
                    onClick={handleGoBack}
                    variant={Button.variant.outline}
                    size={Button.size.md}
                    aria-label="Back"
                    disabled={isLoading}
                >
                    <FormattedMessage id="codeless.back.button" defaultMessage="Back" />
                </Button>
                <div className={styles['save-button-container']}>
                    <Button
                        onClick={handleSaveAndClose}
                        variant={Button.variant.primary}
                        size={Button.size.md}
                        aria-label="Save & close"
                        disabled={isLoading || !hasChangesToSave}
                    >
                        {isLoading && <Spinner size={16} className={styles['spinner']} />}
                        <FormattedMessage id="codeless.save.and.close.button" defaultMessage="Save & close" />
                    </Button>
                </div>
            </footer>

            {isLoading && (
                <div className={styles['central-loading-container']}>
                    <div className={styles['central-loading-content']}>
                        <Spinner size={32} className={styles['central-spinner']} />
                        <span className={styles['central-loading-text']}>
                            <FormattedMessage id="codeless.saving.label" defaultMessage="Saving your events" />
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};
